/* eslint-disable react/jsx-props-no-spreading */

import {
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
} from '@mui/x-data-grid-pro'
import Badge from '@mui/material/Badge'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import React, {
    useState,
} from 'react'

import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import FilterIcon from 'app/shared-components/FilterIcon'

import useStyles from '../SkyNetDataGridToolbar.style'
import ExportToolbarMenu from '../../ExportToolbarMenu'
import {
    SetPanelTargetElFn, ToolbarSharedProps,
} from '../SkyNetDataGridToolbar'
import useClearSorting from '../hooks/useClearSorting'
import ClearSortingIcon from '../../ClearSortingIcon'
import CreateButton from '../CreateButton'
import useAllowedActionsMap, {
    ActionsMap,
} from '../hooks/useAllowedActionsMap'

const actionsMap: ActionsMap = {
    Create: <CreateButton isMenuItem />,
    Print: <div>Print Menu Item</div>,
    BulkCreate: <div>Bulk Create Menu Item</div>,
    BulkEdit: <div>Bulk Edit Menu Item</div>,
}

type Props = ToolbarSharedProps & {
    setPanelTargetEl: SetPanelTargetElFn
}

const ReducedViewToolbarActions = ({
    toolbarControls,
    showFilterButton,
    setPanelTargetEl,
    toolbarActions,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const [
        anchorEl,
        setAnchorEl,
    ] = useState<null | HTMLElement>(null)

    const handleClearSorting = useClearSorting()

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const actions = useAllowedActionsMap({
        actionsMap,
        toolbarActions,
    })

    return (
        <>
            <Tooltip title="Actions">
                <ToolbarButton
                    ref={setPanelTargetEl}
                    onClick={handleClick}
                    data-testid="actions-menu-btn"
                >
                    <MoreVertIcon fontSize="small" />
                </ToolbarButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={classes.dropdown}
                data-testid="actions-menu"
            >
                {actions.Create}
                {toolbarControls?.map((control, index) => {
                    // eslint-disable-next-line react/no-array-index-key
                    return <MenuItem key={`toolbar-control-${index}`}>{control}</MenuItem>
                })}
                {showFilterButton && (
                    <FilterPanelTrigger
                        render={({
                            onClick,
                        }, state) => {
                            return (
                                <MenuItem
                                    onClick={(e) => {
                                        onClick?.(e)
                                        handleClose()
                                    }}
                                    data-testid="filter-menu-item"
                                >
                                    <ListItemIcon>
                                        <Badge
                                            badgeContent={state.filterCount}
                                            color="primary"
                                            variant="dot"
                                        >
                                            <FilterIcon
                                                className={classes.filter}
                                                newStyling
                                            />
                                        </Badge>
                                    </ListItemIcon>
                                    <ListItemText primary="Filters" />
                                </MenuItem>
                            )
                        }}
                    />
                )}
                <ExportToolbarMenu
                    CustomTrigger={(props) => {
                        return (
                            <MenuItem {...props}>
                                <ListItemIcon>
                                    <FileDownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Export" />
                            </MenuItem>
                        )
                    }}
                    onItemClick={handleClose}
                    embeddedInMenu
                />

                <MenuItem
                    onClick={() => {
                        handleClearSorting()
                        handleClose()
                    }}
                    data-testid="clear-sorting-menu-item"
                >
                    <ListItemIcon>
                        <ClearSortingIcon />
                    </ListItemIcon>
                    <ListItemText primary="Clear Sorting" />
                </MenuItem>

                <ColumnsPanelTrigger
                    render={({
                        onClick,
                    }) => {
                        return (
                            <MenuItem
                                onClick={(e) => {
                                    onClick?.(e)
                                    handleClose()
                                }}
                                data-testid="columns-menu-item"
                            >
                                <ListItemIcon>
                                    <ViewColumnIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Columns" />
                            </MenuItem>
                        )
                    }}
                />
            </Menu>
        </>
    )
}

export default ReducedViewToolbarActions
